import { useCallback, useState } from 'react'
import { Button, Col, Container, Form, Row, Spinner } from 'react-bootstrap'
import { IMaskInput } from 'react-imask'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import LoadingSpinner from '../../../../../components/LoadingSpinner/LoadingSpinner'
import SearchInput from '../../../../../components/SearchInput'
import { useLoading } from '../../../../../hooks/LoadingContext'
import DashboardLayout from '../../../../../layouts/Dashboard/index'
import api from '../../../../../services/api'
import { errorApi } from '../../../../../utils/errorApi'

import styles from './styles.module.css'

interface IVehicle {
  id: number
  name: string
}

interface IDriver {
  id: number
  name: string
}

const MultasNova: React.FC = () => {
  const [type, setType] = useState<string>('')
  const [date, setDate] = useState<string>('')
  const [costString, setCostString] = useState<string>('')
  const [cost, setCost] = useState<number>(0)
  const [points, setPoints] = useState<number>()
  const [description, setDescription] = useState<string>('')
  const { isLoading, setIsLoading } = useLoading()
  const [vehicleId, setVehicleId] = useState<number>()
  const [driverId, setDriverId] = useState<number>()
  const [vehicles, setVehicles] = useState<IVehicle[]>()
  const [drivers, setDrivers] = useState<IDriver[]>()

  const [showVehicles, setShowVehicles] = useState<boolean>(false)
  const [showDrivers, setShowDrivers] = useState<boolean>(false)

  const isValidModel = () => {
    if (!(type && date && cost && points && vehicleId && driverId)) return false
    return true
  }

  const navigate = useNavigate()

  const loadDriverData = useCallback(async (searchDriver: string) => {
    setIsLoading(true)
    let apiUrl = '/delivery-manager/drivers?limit=10&show_invalid=1'

    if (searchDriver) apiUrl += '&name=' + searchDriver

    try {
      const { data } = await api.get(apiUrl)

      if (!data.drivers.length) {
        toast.warning('Não foram encontrados resultados para a pesquisa!')
        setDrivers(undefined)
      } else {
        setDrivers(
          data.drivers.map((driver: { user_id: any; name: any }) => {
            return { id: driver.user_id, name: driver.name }
          }),
        )
      }
      setIsLoading(false)
    } catch (err) {
      toast.error('Falha ao buscar os dados! Atualize a página.')
      setIsLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const loadVehicleData = useCallback(async (searchBoard: string) => {
    setIsLoading(true)
    let apiUrl = '/delivery-manager/vehicles?limit=10&show_invalid=1'

    if (searchBoard) apiUrl += '&board=' + searchBoard

    try {
      const { data } = await api.get(apiUrl)

      if (!data.length) {
        toast.warning('Não foram encontrados resultados para a pesquisa!')
        setVehicles(undefined)
      } else {
        setVehicles(
          data.map((vehicle: { id: any; board: any; model: any }) => {
            return {
              id: vehicle.id,
              name: `${vehicle.board} - ${vehicle.model}`,
            }
          }),
        )
      }
      setIsLoading(false)
    } catch (err) {
      toast.error('Falha ao buscar os dados! Atualize a página.')
      setIsLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleVehicleClearInput = () => {
    setShowVehicles(false)
  }

  const handleVehicleSearch = (searchBoard: string) => {
    loadVehicleData(searchBoard)
    setShowVehicles(true)
  }

  const handleDriverClearInput = () => {
    setShowDrivers(false)
  }

  const handleDriverSearch = (searchDriver: string) => {
    loadDriverData(searchDriver)
    setShowDrivers(true)
  }

  const handleSubmit = async (event: any) => {
    event.preventDefault()

    setIsLoading(true)

    try {
      await api.post('/delivery-manager/traffic-tickets', {
        driver_id: driverId,
        vehicle_id: vehicleId,
        type: type,
        description: description,
        date: new Date(date).toISOString(),
        cost: cost,
        points: points,
      })

      toast.info('Multa cadastrada com sucesso!', { autoClose: 3000 })
      setIsLoading(false)
      navigate('/gestor-entregas/veiculos/multas')
    } catch (err) {
      toast.error(errorApi(err, 'Falha ao cadastrar a multa! Verifique os dados.'))
      setIsLoading(false)
    }
  }

  return (
    <DashboardLayout backgroundColor="#F8F8F8" pageName="Cadastrar multa">
      <LoadingSpinner text="Salvando informações. Aguarde..." />
      <Container>
        <h2 style={{ textAlign: 'center' }}>Nova Multa</h2>
        <Col className={styles.formContainer}>
          <Row>
            <Col md={6}>
              <Row style={{ marginTop: 15 }}>
                <Form.Label className={styles.label}>Nome do motorista*</Form.Label>
                <SearchInput
                  data={drivers}
                  showOptions={showDrivers}
                  setValue={(value: number) => setDriverId(value)}
                  handleClear={handleDriverClearInput}
                  handleSearch={handleDriverSearch}
                  placeholder="Digite o nome do motorista"
                  edit={false}
                />
              </Row>
            </Col>

            <Col md={6}>
              <Row style={{ marginTop: 15 }}>
                <Form.Label className={styles.label}>Placa do veículo*</Form.Label>
                <Col style={{ display: 'flex', flexDirection: 'row' }}>
                  <SearchInput
                    data={vehicles}
                    showOptions={showVehicles}
                    setValue={setVehicleId}
                    handleClear={handleVehicleClearInput}
                    handleSearch={handleVehicleSearch}
                    placeholder="Digite a placa do veículo"
                    edit={false}
                  />
                </Col>
              </Row>
            </Col>
          </Row>
          <Row style={{ marginTop: 0, textAlign: 'left' }}>
            <Col md={6} style={{ marginTop: 15 }}>
              <Form.Label className={styles.label}>Tipo da multa*</Form.Label>
              <Form.Control
                className={styles.input}
                placeholder="Digite aqui o tipo da multa"
                value={type}
                onChange={(event) => setType(event.target.value)}
              ></Form.Control>
            </Col>
            <Col md={6} style={{ marginTop: 15 }}>
              <Form.Label className={styles.label}>Data*</Form.Label>
              <Form.Control
                className={styles.input}
                type="datetime-local"
                value={date}
                onChange={(event) => setDate(event.target.value)}
              />
            </Col>
          </Row>
          <Row style={{ marginTop: 0, textAlign: 'left' }}>
            <Col md={6} style={{ marginTop: 15 }}>
              <Form.Label className={styles.label}>Custo*</Form.Label>
              <Form.Control
                className={styles.input}
                as={IMaskInput}
                mask={[
                  {
                    mask: Number,
                    radix: ',',
                  },
                ]}
                placeholder="Digite aqui o custo da multa"
                value={costString}
                onChange={(event) => {
                  setCostString(event.target.value)
                  setCost(Number(event.target.value.replace(',', '.')))
                }}
              ></Form.Control>
            </Col>
            <Col md={6} style={{ marginTop: 15 }}>
              <Form.Label className={styles.label}>Número de pontos*</Form.Label>
              <Form.Control
                className={styles.input}
                placeholder="Digite aqui os pontos na CNH da multa"
                value={points}
                onChange={(event) => setPoints(Number(event.target.value) || 0)}
              ></Form.Control>
            </Col>
          </Row>
          <Row style={{ marginTop: 0, textAlign: 'left' }}>
            <Col style={{ marginTop: 15 }}>
              <Form.Label className={styles.label}>Descrição</Form.Label>
              <Form.Control
                className={styles.input}
                style={{
                  resize: 'none',
                }}
                as="textarea"
                rows={3}
                placeholder="Descrição da multa"
                value={description}
                onChange={(event) => setDescription(event.target.value)}
              />
            </Col>
          </Row>
          <Col style={{ marginTop: 15, textAlign: 'right' }}>
            <Button
              className={styles.btn_submit}
              disabled={isLoading || !isValidModel()}
              onClick={handleSubmit}
            >
              {isLoading ? (
                <Spinner
                  animation="border"
                  size="sm"
                  style={{
                    color: '#fff',
                    marginRight: 10,
                  }}
                />
              ) : (
                'Cadastrar multa'
              )}
            </Button>
          </Col>
        </Col>
      </Container>
    </DashboardLayout>
  )
}

export default MultasNova
